























































































































































// vue
import { defineComponent, ref, watch } from '@vue/composition-api';

// config
import tools from '@/vue3/configs/drawToolsConfigGPS';

// primevue
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';

import SurveyLineStringMode from '@/vue3/mapbox/surveyLineStringMode';
import SurveyPolygonMode from '@/vue3/mapbox/surveyPolygonMode';
import SurveyPointMode from '@/vue3/mapbox/surveyPointMode';
import { useNotify } from '@/vue3/hooks/notifyHook';
import InputSwitch from 'primevue/inputswitch';
import { useStore } from '@/store';

export default defineComponent({
    components: {
        ProgressSpinner,
        Button,
        InputSwitch,
    },
    props: {
        mapbox: {
            type: Object,
            default: null,
        },
        setActiveTool: {
            type: Function,
            default: () => {},
        },
        activeCreateToolName: {
            type: String,
            default: '',
        },
        setActiveCreateToolName: {
            type: Function,
            default: () => {},
        },
        toolOptions: {
            type: Object,
            default: null,
        },
    },
    setup(props: any) {
        //service
        const { warnToast } = useNotify();
        const store = useStore();

        // reactive
        const isSurvey = ref(false);
        const activeToolDesc = ref('');
        const isActive = ref(false);
        const isRecording = ref(false);
        const isLoading = ref(false);
        const isCreateGeorecord = ref(false);

        const gpsWatchInterval = ref<any>();
        const surveyPosition = ref<GeolocationPosition>();
        const showAccuracy =
            store?.state?.accountPermissions?.indexOf(
                'canModuleGPSAccuracy',
            ) !== -1
                ? true
                : false;
        const accuracy = ref();
        const gpsWatchId = ref<number | null>(null); // To store watchPosition ID

        // methods
        const gpsClick = () => {
            if (!isActive.value) {
                isLoading.value = true;
                props.setActiveTool('SelectTool');

                navigator.geolocation.getCurrentPosition(
                    (position: GeolocationPosition) => {
                        surveyPosition.value = position;
                        accuracy.value =
                            Math.round(
                                surveyPosition.value.coords.accuracy * 1000,
                            ) / 1000;
                        isActive.value = true;
                        isLoading.value = false;
                    },
                );
                gpsWatchId.value = navigator.geolocation.watchPosition(
                    (position) => {
                        accuracy.value =
                            Math.round(position.coords.accuracy * 1000) / 1000; // Continuously updates
                    },
                );
                props.mapbox.geoLocateControl.trigger();
            } else {
                clearGps();
            }
        };

        const selectCreateMode = () => {
            props.setActiveCreateToolName(tools[0].name);

            props.setActiveTool('CreateTool');
            store.state.isGPSCreateModeOn = true;
            warnToast(
                'Please turn off create mode to perform any actions on the map.',
            );
        };

        const toolClick = (tool) => {
            props.setActiveCreateToolName(tool.name);
            props.setActiveTool('CreateTool');
            props.mapbox.draw.changeMode(tool.mode);

            activeToolDesc.value = tool.description;

            if (isSurvey.value) {
                clearGps();
            }
        };

        const setIsCreateGeorecord = (): void => {
            isCreateGeorecord.value = !isCreateGeorecord.value;
            if (isCreateGeorecord.value) {
                selectCreateMode();
            } else {
                props.setActiveTool('SelectTool');
            }
        };

        const addClick = () => {
            if (props.activeCreateToolName === 'Point Tool') {
                props.mapbox.draw.changeMode('SurveyPointMode');
                SurveyPointMode.addCoordinates(surveyPosition.value);
            } else if (props.activeCreateToolName === 'Line Tool') {
                if (props.mapbox.draw.getMode() === 'SurveyLineStringMode') {
                    SurveyLineStringMode.retryAddPointAndRender();
                } else {
                    props.mapbox.draw.changeMode('SurveyLineStringMode');
                    isSurvey.value = true;
                    SurveyLineStringMode.retryAddPointAndRender();
                }
            } else if (props.activeCreateToolName === 'Polygon Tool') {
                if (props.mapbox.draw.getMode() === 'SurveyPolygonMode') {
                    SurveyPolygonMode.retryAddPointAndRender();
                } else {
                    props.mapbox.draw.changeMode('SurveyPolygonMode');
                    isSurvey.value = true;
                    SurveyPolygonMode.retryAddPointAndRender();
                }
            }
        };

        const recordClick = () => {
            if (props.mapbox.draw.getMode() === 'SurveyLineStringMode') {
                if (!isRecording.value) {
                    SurveyLineStringMode.StartRecording();
                    isRecording.value = true;
                } else {
                    SurveyLineStringMode.StopRecording();
                    isRecording.value = false;
                }
            } else if (props.mapbox.draw.getMode() === 'SurveyPolygonMode') {
                if (!isRecording.value) {
                    SurveyPolygonMode.StartRecording();
                    isRecording.value = true;
                } else {
                    SurveyPolygonMode.StopRecording();
                    isRecording.value = false;
                }
            } else {
                if (props.activeCreateToolName === 'Line Tool') {
                    props.mapbox.draw.changeMode('SurveyLineStringMode');
                    isSurvey.value = true;
                    SurveyLineStringMode.StartRecording();
                    isRecording.value = true;
                } else if (props.activeCreateToolName === 'Polygon Tool') {
                    props.mapbox.draw.changeMode('SurveyPolygonMode');
                    isSurvey.value = true;
                    SurveyPolygonMode.StartRecording();
                    isRecording.value = true;
                }
            }
        };

        const removeClick = () => {
            if (props.activeCreateToolName === 'Line Tool') {
                SurveyLineStringMode.removeLastPointAndRender();
            } else if (props.activeCreateToolName === 'Polygon Tool') {
                SurveyPolygonMode.removeLastPointAndRender();
            }
        };

        const endSurvey = () => {
            if (props.activeCreateToolName === 'Line Tool') {
                props.mapbox.draw.changeMode('draw_line_string');
                clearGps();
            } else if (props.activeCreateToolName === 'Polygon Tool') {
                props.mapbox.draw.changeMode('draw_polygon');
                clearGps();
            }
        };

        const clearGps = () => {
            props.mapbox.geoLocateControl.trigger();

            if (props.mapbox.geoLocateControl._watchState === 'ACTIVE_LOCK') {
                props.mapbox.geoLocateControl.trigger();
            }
            store.state.isGPSCreateModeOn = false;
            if (gpsWatchId.value !== null) {
                navigator.geolocation.clearWatch(gpsWatchId.value);
                gpsWatchId.value = null;
            }
            // props.setActiveTool('SelectTool');

            isCreateGeorecord.value = false;
            isActive.value = false;
            isSurvey.value = false;
            isRecording.value = false;
        };

        return {
            accuracy,
            showAccuracy,
            gpsClick,
            gpsWatchInterval,
            surveyPosition,
            activeToolDesc,
            isSurvey,
            isRecording,
            isActive,
            toolClick,
            addClick,
            recordClick,
            removeClick,
            endSurvey,
            clearGps,
            tools,
            isLoading,
            isCreateGeorecord,
            setIsCreateGeorecord,
        };
    },
});
